import { Directive, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output } from '@angular/core';

export const IMAGE_SKELETON_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

@Directive({ selector: 'img[businessImage]' })
export class BusinessImageDirective implements OnChanges, OnDestroy {
  @Input() businessImage: string | null | undefined;
  @Input() businessImagePending = false;
  @Input() businessImageEager = false;
  @Input() businessImageFallback = './assets/images/no-image.svg';
  @Output() businessImageError = new EventEmitter<Event>();
  private observer?: IntersectionObserver;
  private request?: HTMLImageElement;
  private timer?: ReturnType<typeof setTimeout>;
  private attempt = 0;
  private generation = 0;
  private destroyed = false;
  private exhausted = false;
  private retryAfter = 0;
  private source = '';
  constructor(private element: ElementRef<HTMLImageElement>) {}

  ngOnChanges(): void {
    this.cancel();
    this.attempt = 0;
    this.exhausted = false;
    this.source = this.businessImage?.trim() || '';
    const missing = !this.source || /(?:^|\/)no-image\.svg(?:[?#]|$)/.test(this.source);
    if (missing && !this.businessImagePending) {
      this.showFallback();
      return;
    }
    this.showSkeleton();
    if (missing) return; // Metadata/crop has not supplied an image URL yet.
    if (this.businessImageEager || typeof IntersectionObserver === 'undefined') { this.load(); return; }
    this.observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      this.observer?.disconnect();
      this.load();
    }, { rootMargin: '300px 0px' });
    this.observer.observe(this.element.nativeElement);
  }

  private showSkeleton(): void {
    this.setBusy(true);
    this.element.nativeElement.src = IMAGE_SKELETON_PIXEL;
  }

  private showFallback(): void {
    this.setBusy(false);
    this.element.nativeElement.src = this.businessImageFallback?.trim() || './assets/images/no-image.svg';
  }

  private setBusy(busy: boolean): void {
    this.element.nativeElement.classList.toggle('business-image-skeleton', busy);
    this.element.nativeElement.setAttribute('aria-busy', String(busy));
  }

  private load(): void {
    if (this.destroyed || !this.source || this.request) return;
    const generation = this.generation;
    const image = new Image();
    this.request = image;
    this.attempt++;
    const finish = (success: boolean, event?: Event) => {
      if (generation !== this.generation || this.destroyed) return;
      clearTimeout(this.timer);
      image.onload = image.onerror = null;
      this.request = undefined;
      if (success) {
        this.exhausted = false;
        this.element.nativeElement.src = this.source;
      } else {
        if (this.attempt < 3) {
          this.timer = setTimeout(() => this.load(), this.attempt === 1 ? 1000 : 3000);
        } else {
          this.exhausted = true;
          this.retryAfter = Date.now() + 30000;
          this.showFallback();
          if (event) this.businessImageError.emit(event);
        }
      }
    };
    image.onload = () => finish(true);
    image.onerror = event => finish(false, event as Event);
    this.timer = setTimeout(() => { finish(false); image.removeAttribute('src'); }, 15000);
    image.src = this.source;
  }

  @HostListener('load')
  onImageLoaded(): void {
    const image = this.element.nativeElement;
    if (image.getAttribute('src') !== IMAGE_SKELETON_PIXEL && image.naturalWidth > 0) this.setBusy(false);
  }

  @HostListener('window:online')
  @HostListener('window:focus')
  recover(): void {
    if (!this.exhausted || this.destroyed || Date.now() < this.retryAfter) return;
    const rect = this.element.nativeElement.getBoundingClientRect();
    if (rect.bottom < -300 || rect.top > window.innerHeight + 300) return;
    this.attempt = 0;
    this.exhausted = false;
    this.showSkeleton();
    this.load();
  }

  private cancel(): void {
    this.generation++;
    this.observer?.disconnect();
    clearTimeout(this.timer);
    if (this.request) {
      this.request.onload = this.request.onerror = null;
      this.request.removeAttribute('src');
      this.request = undefined;
    }
  }

  ngOnDestroy(): void { this.destroyed = true; this.cancel(); }
}
